import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../../../../components/feature/Header';
import Footer from '../../../../components/feature/Footer';

const FreeResources: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-zinc-50">
      <Header />
      
      <article className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Article Header */}
          <div className="mb-12">
            <Link to="/blog" className="inline-flex items-center gap-2 text-teal-600 font-semibold hover:text-teal-700 mb-6 cursor-pointer">
              <i className="ri-arrow-left-line"></i>
              Back to Blog
            </Link>
            <div className="mb-6">
              <span className="bg-teal-600 text-white px-4 py-2 rounded-full text-sm font-semibold">
                Free Resources
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              Free Resources: Templates, Guides &amp; Tools for Small Businesses
            </h1>
            <div className="flex items-center gap-6 text-gray-600 mb-8">
              <span className="flex items-center gap-2">
                <i className="ri-calendar-line"></i>
                February 20, 2024
              </span>
              <span className="flex items-center gap-2">
                <i className="ri-time-line"></i>
                4 min read
              </span>
            </div>
            <div className="relative w-full h-96 rounded-2xl overflow-hidden mb-8">
              <img
                src="https://public.readdy.ai/ai/img_res/39f95a4b010751142bb05bad03bfed15.jpg"
                alt="Free Resources"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          {/* Article Content */}
          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-gray-700 leading-relaxed mb-8">
              Running a small business requires juggling multiple responsibilities, and having the right resources can make all the difference. We've compiled a comprehensive collection of free templates, guides, and tools to help you streamline your marketing, operations, and growth strategies.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Marketing Templates</h2>
            
            <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Social Media Content Calendar Template</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Plan your social media content for the entire month with our easy-to-use spreadsheet template. Includes:
            </p>
            <ul className="list-disc pl-6 mb-6 text-gray-700 space-y-2">
              <li>Monthly planning grid for all major platforms</li>
              <li>Content category tracking</li>
              <li>Hashtag research section</li>
              <li>Performance tracking columns</li>
              <li>Holiday and awareness day calendar</li>
            </ul>

            <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Email Marketing Campaign Template</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Create effective email campaigns with our proven templates:
            </p>
            <ul className="list-disc pl-6 mb-6 text-gray-700 space-y-2">
              <li>Welcome email sequence (5 emails)</li>
              <li>Product launch announcement</li>
              <li>Newsletter template</li>
              <li>Re-engagement campaign</li>
              <li>Customer feedback request</li>
            </ul>

            <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Marketing Budget Template</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              Track and allocate your marketing spend across channels with built-in ROI calculators and monthly budget tracking.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Business Planning Guides</h2>

            <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Complete Digital Marketing Strategy Guide</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              A 30-page comprehensive guide covering:
            </p>
            <ul className="list-disc pl-6 mb-6 text-gray-700 space-y-2">
              <li>Setting SMART marketing goals</li>
              <li>Identifying your target audience</li>
              <li>Choosing the right marketing channels</li>
              <li>Creating a content strategy</li>
              <li>Measuring and optimizing performance</li>
            </ul>

            <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Local SEO Optimization Checklist</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Step-by-step checklist to improve your local search rankings:
            </p>
            <ul className="list-disc pl-6 mb-6 text-gray-700 space-y-2">
              <li>Google My Business optimization</li>
              <li>Local citation building</li>
              <li>On-page SEO elements</li>
              <li>Review generation strategies</li>
              <li>Local link building tactics</li>
            </ul>

            <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Website Launch Checklist</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              Ensure your website is ready for launch with our comprehensive 50-point checklist covering design, functionality, SEO, and legal requirements.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Design Resources</h2>

            <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Social Media Graphics Templates</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Canva templates for all major platforms:
            </p>
            <ul className="list-disc pl-6 mb-6 text-gray-700 space-y-2">
              <li>Instagram posts and stories</li>
              <li>Facebook cover photos and posts</li>
              <li>LinkedIn banners and posts</li>
              <li>Twitter headers and graphics</li>
              <li>Pinterest pins</li>
            </ul>

            <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Brand Style Guide Template</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              Document your brand identity with sections for logo usage, color palette, typography, imagery guidelines, and voice/tone.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Customer Service Resources</h2>

            <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Customer Service Response Templates</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Pre-written, customizable responses for common scenarios:
            </p>
            <ul className="list-disc pl-6 mb-6 text-gray-700 space-y-2">
              <li>Order confirmation and shipping updates</li>
              <li>Handling complaints and refunds</li>
              <li>Responding to positive reviews</li>
              <li>Addressing negative feedback</li>
              <li>FAQ responses</li>
            </ul>

            <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Customer Feedback Survey Template</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              Google Forms template with proven questions to gather actionable customer insights and measure satisfaction.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Analytics &amp; Reporting</h2>

            <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Monthly Marketing Report Template</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Professional report template to track:
            </p>
            <ul className="list-disc pl-6 mb-6 text-gray-700 space-y-2">
              <li>Website traffic and conversions</li>
              <li>Social media performance</li>
              <li>Email marketing metrics</li>
              <li>Paid advertising ROI</li>
              <li>Key insights and recommendations</li>
            </ul>

            <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Google Analytics Dashboard Template</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              Pre-configured dashboard to track your most important website metrics at a glance.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Content Creation Tools</h2>

            <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Blog Post Outline Template</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              Structured template to plan engaging blog posts with sections for headlines, introduction, main points, conclusion, and SEO optimization.
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Video Script Template</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              Create compelling video content with our script template including hook, main content, and call-to-action sections.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">How to Access These Resources</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              All resources are completely free and available for immediate download. Simply contact us at the email below, and we'll send you access to our entire resource library. We regularly add new templates and guides, so check back often!
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Need Customized Resources?</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              While these templates are designed to work for most small businesses, we understand that every business is unique. If you need customized templates, strategies, or guidance specific to your industry or situation, we're here to help.
            </p>

            {/* Author Bio */}
            <div className="bg-gray-100 rounded-2xl p-8 mt-12 mb-8">
              <div className="flex items-start gap-6">
                <div className="w-24 h-24 flex items-center justify-center bg-teal-600 rounded-full flex-shrink-0">
                  <span className="text-3xl font-bold text-white">KH</span>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Kimberly Herring</h3>
                  <p className="text-teal-600 font-semibold mb-3">Founder, Digital Experience Studio</p>
                  <p className="text-gray-700 leading-relaxed">
                    Kimberly is passionate about empowering small businesses with the tools and knowledge they need to succeed. She regularly creates and shares free resources to help entrepreneurs grow their businesses without breaking the bank.
                  </p>
                </div>
              </div>
            </div>

            {/* Contact CTA */}
            <div className="bg-gradient-to-r from-teal-600 to-emerald-600 rounded-2xl p-8 text-white mt-12">
              <h3 className="text-2xl font-bold mb-4">Get Access to All Free Resources</h3>
              <p className="text-teal-100 mb-6">
                Email us to receive instant access to our complete library of free templates, guides, and tools.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mb-6">
                <a
                  href="mailto:letschat@socialswingstudio.com?subject=Free Resources Request"
                  className="inline-block bg-white text-teal-600 px-6 py-3 rounded-full font-bold hover:bg-gray-100 transition-colors text-center whitespace-nowrap cursor-pointer"
                >
                  Request Free Resources
                </a>
                <a
                  href="https://www.digitalexperience.studio"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block border-2 border-white text-white px-6 py-3 rounded-full font-bold hover:bg-white hover:text-teal-600 transition-colors text-center whitespace-nowrap cursor-pointer"
                >
                  Book Free Consultation
                </a>
              </div>
              <p className="text-sm text-teal-100">
                <strong>Written by Kimberly Herring, Founder of Digital Experience Studio</strong><br />
                Contact: letschat@socialswingstudio.com
              </p>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default FreeResources;